// The in-memory API's agent policy (D10 scope editor; services/api/src/routes/seats-policy.ts,
// domain/agent-policy/*) with the real routes' rules: behind the `agents` add-on; managers and the
// holder of the seat's supervisor seat read, managers edit; `PUT` takes `If-Match` on a content
// version (428 without, 409 with the current policy when stale); unknown classes and the hard
// prohibitions are 422 with nothing written; `inherit` removes the seat's row, so an empty policy
// is every class's default; the preview of an edit (`modes=`) and the explainer run the same rule
// as the policy view (`decide` below, the twin of decide.ts `decideAgentActionPure`).
//
// The twin serves the seats of its DEs. It has no board rows or fences (Board settings › Agents is
// P4) and no legacy seat keys; each DE's seat holds one agent key with the scopes every agent key
// got in Phase 1 (`runs:*`, `kpis:*`, `files:*`, `tasks:*`, `boards:*`), beside its ingest keys.
import type { OperationOutput, RouteId } from "@companyos/sdk";
import {
  featureDisabled,
  type Handler,
  json,
  notFound,
  type Principal,
  problem,
  validation,
} from "./fake";
import { membershipOf } from "./fake-chat";
import type { FakeDe } from "./fake-data";

type View = OperationOutput<"seats.agentPolicy">;
type Preview = OperationOutput<"seats.agentPolicyPreview">;
type Explain = OperationOutput<"seats.agentPolicyExplain">;
type Mode = "direct" | "ask" | "never";
type Stored = Mode | "inherit";
type Source = Preview["classes"][number]["source"];
type Step = Explain["steps"][number];

/** The §8.1 table (classes.ts `CLASS_TABLE`): default, operations, the route's hard limit. */
export const FAKE_CLASSES: Record<string, { default: Mode; operations: string; limit?: string }> = {
  read: {
    default: "direct",
    operations: "list/get/search/read_doc/get_thread/list_fields/get_app",
    limit: "a read fence hides the board",
  },
  comment: {
    default: "direct",
    operations: "add_comment, post_message, react, edit own message",
    limit: "the thread gate per seat",
  },
  task_create: { default: "direct", operations: "create_task, subtasks, from template" },
  assign: {
    default: "ask",
    operations: "assign a person (own seat or another agent seat: field_set, Direct)",
  },
  field_set: {
    default: "direct",
    operations:
      "set_field, update_task (title, dates, priority, estimate, description), tag, checklist, link, mirror",
    limit: "fields flagged requires_approval are proposed",
  },
  upload: { default: "direct", operations: "upload/rename attachment, record observation" },
  status_own: {
    default: "direct",
    operations: "status/complete/reopen on tasks assigned or delegated to the seat",
  },
  status_other: { default: "direct", operations: "non-terminal status on other tasks" },
  complete_other: {
    default: "ask",
    operations: "terminal status / reopen on tasks not assigned to the seat",
    limit: "05: always proposed",
  },
  reorder: { default: "direct", operations: "move_task {position} within a board; claim_task" },
  task_move: { default: "ask", operations: "move_task to another board" },
  task_delete: { default: "ask", operations: "delete_task (to the trash)", limit: "never purge" },
  restore: { default: "ask", operations: "restore_task" },
  file_delete: { default: "ask", operations: "delete_attachment" },
  board_config: {
    default: "ask",
    operations:
      "set_statuses affecting tasks, rename/create/delete field (set_statuses moving no task: Direct)",
  },
  automation_write: {
    default: "ask",
    operations: "create/delete/toggle automation, propose_routine",
  },
  playbook: {
    default: "direct",
    operations: "start_journey, complete own steps (a gate is always a playbook_gate proposal)",
    limit: "the gate invariant",
  },
  page_edit: {
    default: "direct",
    operations: "edit pages (kind sop/policy: Ask)",
    limit: "05",
  },
  bulk: {
    default: "ask",
    operations: "one operation whose computed affected set is over 10",
    limit: "the set is bound to the approval",
  },
  external: {
    default: "ask",
    operations:
      "gmail_send, slack_post, github writes, api_request, webhook egress, calendar with attendees",
    limit: "the connection's mode narrows",
  },
  delegation: {
    default: "ask",
    operations:
      "delegate beyond depth 1; budget beyond the soft limit (depth 1 within budget: Direct)",
    limit: "05",
  },
  // CALENDAR-C1: the API's `calendar` class (a DE proposes; it never writes a calendar directly).
  calendar: {
    default: "ask",
    operations: "propose_calendar_change (create, move, cancel an event); own RSVP is Direct",
    limit: "a DE never writes directly; an owner's assistant only with `calendar` autonomy alone",
  },
};

/** classes.ts `NEVER_TEXT`: the hard prohibitions. */
export const FAKE_NEVER: Record<string, string> = {
  access_grant: "grant or revoke access",
  key_manage: "mint or revoke keys (rotating its own key is the exception)",
  approval_decide: "decide approvals",
  layout_edit: "change a task or space layout",
  archive: "archive spaces and boards",
  purge: "purge from the trash",
  member_manage: "add or remove members",
  publish: "publish a playbook, form or dashboard",
  agent_policy_edit: "change the agent policy",
};

/** classes.ts `ENFORCED_CLASSES`: the classes a seat setting can be given today. */
export const FAKE_ENFORCED = ["status_own", "status_other", "complete_other", "task_move"];
/** classes.ts `SETTABLE_MODES`. */
const SETTABLE: Record<string, Mode[]> = {
  status_own: ["direct", "ask", "never"],
  status_other: ["direct", "ask", "never"],
  complete_other: ["ask", "never"],
  task_move: ["direct", "ask", "never"],
};

type Verdict = Preview["classes"][number]["verdict"];
/** classes.ts `inPractice` (for an enforced class): what the agent meets, and why. */
function inPractice(
  cls: string,
  v: Verdict,
  level: string,
): { in_practice: Verdict; note: string | null } {
  if (!FAKE_ENFORCED.includes(cls)) return { in_practice: v, note: null };
  if (cls === "task_move" && v === "propose" && level === "default")
    return {
      in_practice: "allow_in_run",
      note: "The class default (Ask) is not held yet: until Ask or Never is set here, the agent moves tasks to other boards on its own.",
    };
  if (
    cls === "complete_other" &&
    ((level === "default" && v === "propose") || (level === "legacy" && v === "allow_in_run"))
  )
    return {
      in_practice: "allow_in_run",
      note: "Not held on every path yet: a status change is proposed (05), but moving the task onto a done status, or changing the board's status set, completes or reopens it directly until Ask or Never is set here.",
    };
  return { in_practice: v, note: null };
}

/** classes.ts `MCP_TOOL_CLASS` (`run` = a run's own tool, not a policy decision). */
export const FAKE_TOOLS: Record<string, string> = {
  list_spaces: "read",
  list_tasks: "read",
  get_task: "read",
  create_container: "board_config",
  set_statuses: "board_config",
  create_task: "task_create",
  update_task: "field_set",
  set_field: "field_set",
  rename_field: "board_config",
  record_observation: "upload",
  get_observations: "read",
  add_comment: "comment",
  complete_task: "status_own",
  reopen_task: "status_own",
  move_task: "reorder",
  my_tasks: "read",
  advance_journey: "playbook",
  list_attachments: "read",
  read_attachment: "read",
  upload_attachment: "upload",
  rename_attachment: "upload",
  delete_attachment: "file_delete",
  // CHAT-FILES
  read_chat_attachment: "read",
  upload_chat_attachment: "upload",
  save_chat_attachment: "upload",
  list_automations: "read",
  create_automation: "automation_write",
  delete_automation: "automation_write",
  search: "read",
  list_docs: "read",
  get_context: "read",
  read_doc: "read",
  list_threads: "read",
  get_thread: "read",
  get_conversation: "read",
  post_message: "comment",
  start_agent_work: "delegation",
  inspect_agent_run: "read",
  respond_to_agent_run: "run",
  my_inbox: "read",
  checkout: "run",
  heartbeat: "run",
  checkpoint: "run",
  reserve: "run",
  report_usage: "run",
  ask_input: "run",
  propose: "run",
  complete_run: "run",
  release_run: "run",
  delegate: "delegation",
  await_children: "run",
  settle_attempt: "run",
  abort_attempt: "run",
  list_routines: "read",
  propose_routine: "automation_write",
  get_app: "read",
  propose_app: "run",
  kpis_read: "read",
  outcomes_record: "upload",
};
/** classes.ts `MCP_CALL_CLASSES`: tools whose class depends on the call. */
const CALL_TOOLS = ["update_task", "complete_task", "reopen_task", "move_task", "complete_run"];

type Need = { family: string; access: "read" | "write" };
const W = (family: string): Need => ({ family, access: "write" });
const R = (family: string): Need => ({ family, access: "read" });
/** seat-policy.ts `workNeed`: per class (`varies`: no single answer) and per tool. */
const NEEDS: Record<string, Need | "varies" | undefined> = {
  read: "varies",
  upload: "varies",
  board_config: "varies",
  task_create: W("tasks"),
  assign: W("tasks"),
  field_set: W("tasks"),
  status_own: W("tasks"),
  status_other: W("tasks"),
  complete_other: W("tasks"),
  reorder: W("tasks"),
  task_move: W("tasks"),
  task_delete: W("tasks"),
  restore: W("tasks"),
  file_delete: W("files"),
  bulk: W("tasks"),
};
const TOOL_NEEDS: Record<string, Need> = {
  list_tasks: R("tasks"),
  get_task: R("tasks"),
  my_tasks: R("tasks"),
  create_task: W("tasks"),
  set_field: W("tasks"),
  set_statuses: W("boards"),
  rename_field: W("boards"),
  list_attachments: R("files"),
  read_attachment: R("files"),
  upload_attachment: W("files"),
  rename_attachment: W("files"),
  delete_attachment: W("files"),
  // CHAT-FILES
  read_chat_attachment: R("files"),
  upload_chat_attachment: W("files"),
  save_chat_attachment: W("files"),
};

const RANK: Record<Mode, number> = { direct: 0, ask: 1, never: 2 };
const VERDICT = { direct: "allow_in_run", ask: "propose", never: "deny" } as const;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const CLASS_NAME = /^[a-z][a-z_]{0,63}$/;
const MODES: readonly Stored[] = ["inherit", "direct", "ask", "never"];
const SCOPES = ["boards:*", "files:*", "kpis:*", "runs:*", "tasks:*"];

/** decide.ts over the twin's rows: never → unknown → seat or default → ceiling (tightens). */
function decide(
  cls: string,
  seatRows: ReadonlyMap<string, Stored>,
  ceiling: ReadonlyMap<string, Mode>,
  companyId: string,
  seatId: string,
  affected: number | null,
): { verdict: Preview["classes"][number]["verdict"]; source: Source; steps: Step[] } {
  const steps: Step[] = [];
  const step = (level: Step["level"], mode: Mode | null, applied: boolean, id: string | null) =>
    steps.push({ level, scope_id: id, mode, applied });
  const done = (level: Source["level"], mode: Mode, id: string | null) => ({
    verdict: VERDICT[mode],
    source: { level, scope_id: id, mode },
    steps,
  });
  const never = cls in FAKE_NEVER;
  step("never", never ? "never" : null, never, null);
  if (never) return done("never", "never", null);
  step("fence", null, false, null);
  const def = FAKE_CLASSES[cls];
  step("unknown_class", def ? null : "ask", !def, null);
  if (!def) return done("unknown_class", "ask", null);
  const own = seatRows.get(cls);
  const seatMode = own && own !== "inherit" ? own : null;
  let out: { level: Source["level"]; mode: Mode; id: string | null } = seatMode
    ? { level: "seat", mode: seatMode, id: seatId }
    : { level: "default", mode: def.default, id: null };
  step("default", def.default, !seatMode, null);
  step("legacy", null, false, seatId);
  step("seat", seatMode, !!seatMode, seatId);
  const c = ceiling.get(cls) ?? null;
  const wins =
    c !== null && (RANK[c] > RANK[out.mode] || (c === out.mode && out.level === "default"));
  if (wins && c) out = { level: "company", mode: c, id: companyId };
  step("company", c, wins, companyId);
  if ((affected ?? 0) > 10 && cls !== "bulk" && cls !== "read") {
    const b = decide("bulk", seatRows, ceiling, companyId, seatId, null).source;
    const tightens =
      RANK[b.mode] > RANK[out.mode] ||
      (b.mode === out.mode && out.level === "default" && b.level !== "default");
    if (tightens) out = { level: "bulk", mode: b.mode, id: b.scope_id };
    step("bulk", b.mode, tightens, b.scope_id);
  }
  return done(out.level, out.mode, out.id);
}

/** The version: the seat's rows, digested (FNV-1a; the server uses sha256). */
function versionOf(rows: ReadonlyMap<string, Stored>): string {
  const text = JSON.stringify([...rows.entries()].sort(([a], [b]) => (a < b ? -1 : 1)));
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) h = Math.imul(h ^ text.charCodeAt(i), 0x01000193) >>> 0;
  return `p1-${h.toString(16).padStart(8, "0")}`;
}

/** seat-policy.ts `modeProblems`, as one detail line. */
function modeProblem(modes: Record<string, unknown>): string | null {
  for (const [cls, mode] of Object.entries(modes)) {
    if (cls in FAKE_NEVER) return `modes.${cls}: ${cls} is a hard prohibition`;
    if (!(cls in FAKE_CLASSES)) return `modes.${cls}: ${cls} is not an action class`;
    if (!MODES.includes(mode as Stored)) return `modes.${cls}: inherit, direct, ask or never`;
    if (mode !== "inherit" && !FAKE_ENFORCED.includes(cls))
      return `modes.${cls}: ${cls} is not held on every write path yet: it cannot be set`;
    if (mode !== "inherit" && !SETTABLE[cls]?.includes(mode as Mode))
      return `modes.${cls}: ${cls} cannot be ${mode}: a route rule holds it anyway`;
  }
  return null;
}

export function createAgentPolicyHandlers(deps: {
  now: () => Date;
  featureOn: (p: Principal, feature: string) => boolean;
  desOf: (companyId: string) => FakeDe[];
  /** Whether the board is one of the caller's company's (the work twin's `boards.get`). */
  boardExists: (p: Principal, boardId: string) => boolean;
  /** The task's board, or null when the caller's company has no such task (`tasks.get`). */
  taskBoardOf: (p: Principal, taskId: string) => string | null;
}): Partial<Record<RouteId, Handler>> {
  /** Seat rows per seat id; the company ceiling per company (none in the twin's seed). */
  const seatRows = new Map<string, Map<string, Stored>>();
  const ceilings = new Map<string, Map<string, Mode>>();
  const rowsOf = (seatId: string) => {
    let m = seatRows.get(seatId);
    if (!m) {
      m = new Map();
      seatRows.set(seatId, m);
    }
    return m;
  };
  const ceilingOf = (co: string) => ceilings.get(co) ?? new Map<string, Mode>();

  /** The DE on the seat and the caller's standing; a Response refuses. */
  const admit = (
    p: Principal | null,
    seatId: string,
    edit: boolean,
  ): Response | { de: FakeDe; manager: boolean } => {
    if (!p) return notFound();
    if (!deps.featureOn(p, "agents")) return featureDisabled("agents");
    const de = deps
      .desOf(p.company.id)
      .find((d) => d.seat_id === seatId.toLowerCase() && !d.removed_at);
    if (p.manager) return de ? { de, manager: true } : notFound();
    const mine = membershipOf(p.me, p.company.id);
    if (!edit && de && mine && de.supervisor.membership_id === mine) return { de, manager: false };
    return problem(403, "forbidden", "Forbidden", "managers and the agent's supervisor only");
  };

  const keysOf = (de: FakeDe): View["keys"] => [
    {
      id: `${de.seat_id.slice(0, 24)}${"a".repeat(12)}`,
      key_prefix: `cosk_ag_${de.membership_id.slice(0, 4)}`,
      scopes: SCOPES,
      expires_at: null,
      created_at: "2026-06-01T09:00:00.000Z",
      work: {
        tasks: { read: true, write: true },
        boards: { read: true, write: true },
        files: { read: true, write: true },
      },
    },
  ];

  const view = (co: string, de: FakeDe, manager: boolean): View => {
    const rows = rowsOf(de.seat_id);
    const ceiling = ceilingOf(co);
    return {
      seat_id: de.seat_id,
      version: versionOf(rows),
      classes: Object.entries(FAKE_CLASSES).map(([cls, def]) => {
        const d = decide(cls, rows, ceiling, co, de.seat_id, null);
        return {
          class: cls as View["classes"][number]["class"],
          operations: def.operations,
          limit: def.limit ?? null,
          default_mode: def.default,
          seat_mode: rows.get(cls) ?? "inherit",
          legacy_mode: null,
          ceiling_mode: ceiling.get(cls) ?? null,
          enforced: FAKE_ENFORCED.includes(cls),
          settable_modes: SETTABLE[cls] ?? [],
          verdict: d.verdict,
          source: d.source,
          ...inPractice(cls, d.verdict, d.source.level),
        };
      }),
      unknown_rows: [],
      never: Object.values(FAKE_NEVER),
      keys: keysOf(de),
      editable: manager,
    };
  };

  /** The boards named, each of the caller's company (another company's or none: 404). */
  const boardsOf = (p: Principal, raw: string | null): string[] | Response => {
    if (!raw) return [];
    const ids = [
      ...new Set(
        raw
          .split(",")
          .map((s) => s.trim().toLowerCase())
          .filter(Boolean),
      ),
    ];
    if (ids.length > 50 || ids.some((i) => !UUID.test(i)))
      return validation("boardIds: board ids are uuids (at most 50)");
    if (ids.some((i) => !deps.boardExists(p, i))) return notFound();
    return ids;
  };

  return {
    "seats.agentPolicy": ({ params, principal }) => {
      const a = admit(principal, params.id ?? "", false);
      if (a instanceof Response) return a;
      return json(view(params.co ?? "", a.de, a.manager));
    },

    "seats.agentPolicyUpdate": ({ params, principal, body, headers }) => {
      const a = admit(principal, params.id ?? "", true);
      if (a instanceof Response) return a;
      const raw = headers.get("if-match");
      if (raw === null) return problem(428, "precondition_required", "If-Match required");
      const modes = (body as { modes?: Record<string, unknown> } | undefined)?.modes ?? {};
      if (Object.keys(modes).length === 0) return validation("modes: at least one class");
      const bad = modeProblem(modes);
      if (bad) return validation(bad);
      const co = params.co ?? "";
      const rows = rowsOf(a.de.seat_id);
      const expected = raw
        .trim()
        .replace(/^W\//, "")
        .replace(/^"(.*)"$/, "$1");
      if (expected !== versionOf(rows))
        return problem(409, "conflict", "Conflict", "the policy changed since it was read", {
          current: view(co, a.de, true),
        });
      for (const [cls, mode] of Object.entries(modes))
        if (mode === "inherit") rows.delete(cls);
        else rows.set(cls, mode as Mode);
      return json(view(co, a.de, true));
    },

    "seats.agentPolicyPreview": ({ params, principal, url }) => {
      const a = admit(principal, params.id ?? "", false);
      if (a instanceof Response) return a;
      const boards = boardsOf(principal as Principal, url.searchParams.get("boardIds"));
      if (boards instanceof Response) return boards;
      const proposed = new Map(rowsOf(a.de.seat_id));
      const raw = url.searchParams.get("modes");
      if (raw) {
        const modes: Record<string, string> = {};
        for (const pair of raw.split(",")) {
          const [cls = "", mode = "", ...rest] = pair.trim().split(":");
          if (!cls || rest.length > 0) return validation("modes: class:mode pairs");
          modes[cls] = mode;
        }
        const bad = modeProblem(modes);
        if (bad) return validation(bad);
        for (const [cls, mode] of Object.entries(modes))
          if (mode === "inherit") proposed.delete(cls);
          else proposed.set(cls, mode as Mode);
      }
      const co = params.co ?? "";
      const body: Preview = {
        seat_id: a.de.seat_id,
        board_ids: boards,
        classes: Object.entries(FAKE_CLASSES).map(([cls, def]) => {
          const d = decide(cls, proposed, ceilingOf(co), co, a.de.seat_id, null);
          return {
            class: cls as Preview["classes"][number]["class"],
            verdict: d.verdict,
            source: d.source,
            default_mode: def.default,
            in_practice: inPractice(cls, d.verdict, d.source.level).in_practice,
          };
        }),
        never: Object.values(FAKE_NEVER),
        fences: [],
      };
      return json(body);
    },

    "seats.agentPolicyExplain": ({ params, principal, url }) => {
      const a = admit(principal, params.id ?? "", false);
      if (a instanceof Response) return a;
      const q = url.searchParams;
      const tool = q.get("tool");
      const asked = q.get("class");
      if ((tool === null) === (asked === null)) return validation("class: name a class or a tool");
      let cls = asked ?? "";
      if (tool !== null) {
        const t = FAKE_TOOLS[tool];
        if (!t) return validation("tool: not an agent tool");
        if (t === "run") return validation("tool: a run's own tool: not a policy decision");
        if (CALL_TOOLS.includes(tool))
          return validation("tool: its class depends on the call: ask about the class");
        cls = t;
      } else if (!CLASS_NAME.test(cls)) return validation("class: a class name");
      const p = principal as Principal;
      const boards = boardsOf(p, q.get("boardIds"));
      if (boards instanceof Response) return boards;
      const taskId = q.get("taskId");
      if (taskId !== null) {
        if (!UUID.test(taskId)) return validation("taskId: a uuid");
        const b = deps.taskBoardOf(p, taskId)?.toLowerCase();
        if (!b) return notFound();
        if (!boards.includes(b)) boards.push(b);
      }
      const rawCount = q.get("affectedCount");
      const affected = rawCount === null ? null : Number(rawCount);
      if (affected !== null && (!Number.isInteger(affected) || affected < 0 || affected > 100_000))
        return validation("affectedCount: an integer from 0 to 100000");
      const co = params.co ?? "";
      const d = decide(cls, rowsOf(a.de.seat_id), ceilingOf(co), co, a.de.seat_id, affected);
      const toolNeed = tool !== null ? TOOL_NEEDS[tool] : undefined;
      const classNeed = NEEDS[cls];
      const varies = !toolNeed && classNeed === "varies";
      const needs: Need[] = toolNeed
        ? tool === "set_statuses"
          ? [W("boards"), R("tasks"), W("tasks")] // boards.ts: a status set rewrites tasks
          : [toolNeed]
        : classNeed && classNeed !== "varies"
          ? [classNeed]
          : [];
      const body: Explain = {
        seat_id: a.de.seat_id,
        class: cls,
        tool,
        board_ids: boards,
        affected_count: affected,
        enforced: FAKE_ENFORCED.includes(cls),
        verdict: d.verdict,
        source: d.source,
        refusal:
          d.verdict === "deny"
            ? {
                code: "never_class",
                message:
                  d.source.level === "never"
                    ? `Agents may never ${FAKE_NEVER[cls]}`
                    : "Agents may never do this; it cannot be approved",
                data: { class: cls },
              }
            : null,
        ...inPractice(cls, d.verdict, d.source.level),
        steps: d.steps,
        work: {
          needs,
          varies,
          // the twin's one key holds every work scope
          keys: keysOf(a.de).map((k) => ({
            id: k.id,
            key_prefix: k.key_prefix,
            allowed: varies ? null : true,
          })),
        },
      };
      return json(body);
    },
  };
}
